import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

export type ToastType = 'success' | 'error' | 'info'

interface ToastItem {
  id: string
  message: string
  type: ToastType
}

interface ToastApi {
  success: (message: string) => void
  error: (message: string) => void
  info: (message: string) => void
}

const ToastApiContext = createContext<ToastApi | null>(null)

export function useToast(): ToastApi {
  const ctx = useContext(ToastApiContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

let globalAddToast: ((message: string, type: ToastType) => void) | null = null

export const toast: ToastApi = {
  success: (message: string) => globalAddToast?.(message, 'success'),
  error: (message: string) => globalAddToast?.(message, 'error'),
  info: (message: string) => globalAddToast?.(message, 'info'),
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const addToast = useCallback((message: string, type: ToastType) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    setToasts((prev) => [...prev, { id, message, type }])
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  useEffect(() => {
    globalAddToast = addToast
    return () => { globalAddToast = null }
  }, [addToast])

  const api = useRef<ToastApi>({
    success: (m) => addToast(m, 'success'),
    error: (m) => addToast(m, 'error'),
    info: (m) => addToast(m, 'info'),
  }).current

  return (
    <ToastApiContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2.5">
          {toasts.map((toast) => (
            <ToastCard
              key={toast.id}
              toast={toast}
              onDismiss={() => removeToast(toast.id)}
            />
          ))}
        </div>,
        document.body
      )}
    </ToastApiContext.Provider>
  )
}

function ToastCard({
  toast,
  onDismiss,
}: {
  toast: ToastItem
  onDismiss: () => void
}) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 4000)
    return () => clearTimeout(timer)
  }, [onDismiss])

  const borderColor =
    toast.type === 'success'
      ? 'border-l-leaf'
      : toast.type === 'error'
        ? 'border-l-ember'
        : 'border-l-haze'

  const iconColor =
    toast.type === 'success'
      ? 'text-leaf'
      : toast.type === 'error'
        ? 'text-ember'
        : 'text-mute'

  const icon =
    toast.type === 'success'
      ? '✓'
      : toast.type === 'error'
        ? '✕'
        : '◦'

  return (
    <div
      className={`animate-slide-in-right flex items-center gap-3 px-4 py-3.5 bg-ink-raised border-l-[3px] ${borderColor} min-w-[260px] max-w-sm`}
      role="alert"
    >
      <span className={`text-sm font-medium ${iconColor} w-5 h-5 flex items-center justify-center bg-current/10`}>
        {icon}
      </span>
      <span className="text-sm font-medium text-cream">{toast.message}</span>
      <button
        onClick={onDismiss}
        className="ml-auto text-mute hover:text-cream transition-colors text-xs w-6 h-6 flex items-center justify-center hover:bg-haze/30"
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  )
}
